import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import CategoryIcon, { CategoryIconDisc } from "../components/categoryIcons";
import { getCatalogCounts, resolvePermalink } from "../api/redesignApi";
import { paths } from "../routing/paths";

/*
 * Categories index per `Categories Index.dc.html` (revised, brief v2.2
 * §7.2.5) — the "Browse all" front door.
 *
 * The 13 categories are an EDITORIAL fixed list — one flat list in the
 * reference's exact order, never grouped into families, never reordered;
 * names, one-liners, and action-link text are verbatim from the
 * reference. The raw taxonomy can't supply this: it has 20 roots
 * including junk ("Late Summer", "Curriculum"), long-form descriptions
 * instead of one-liners, and one of the 13 (Lives of the Chabad
 * Rebbeim) isn't a root at all. Each entry maps to its real archive
 * node (verified live) for links, counts, and the archive subtree.
 *
 * Live data stays live: archive-view counts come from /catalog/counts
 * per node (never the reference's sample numbers), and subtree children
 * come from /catalog/roots. Learning and archive views show the same 13
 * items in the same order.
 */

const VIEW_KEY = "ic-catindex-view";
const COUNT_FANOUT_CAP = 40;

// Verbatim from the reference (name / blurb / scale); slug = the real
// archive node each card opens (verified against the live taxonomy).
export const CATEGORIES = [
  { key: "parsha", title: "Parsha", blurb: "Sichos, Ma'amarim and Parsha thoughts for each week", scale: "54 parshiyos", slug: "weekly-parsha" },
  { key: "calendar", title: "Jewish and Chassidic Calendar", blurb: "Every special date of the year and its meaning", scale: "12 months", slug: "jewish-and-chassidic-calendar" },
  { key: "tanya", title: "Tanya", blurb: "The foundational text of Chabad Chassidus, taught at every level", scale: "8 series", slug: "tanya" },
  { key: "hayomyom", title: "Hayom Yom", blurb: "The Rebbe's daily teachings. Explained in detail.", scale: "Daily study", slug: "hayom-yom-2" },
  { key: "maamarim", title: "Ma'amarim", blurb: "Organized by Themes, Rebbe and year said", scale: "7 collections", slug: "maamarim-2" },
  { key: "sichos", title: "Sichos", blurb: "The Rebbe's talks, by theme or volume. In-depth or all-in-one classes.", scale: "Start learning", slug: "sichos" },
  { key: "tefilla", title: "Tefilla (Prayer)", blurb: "The service of the heart, from the inside", scale: "Inspire your Davening", slug: "tefilla-prayer" },
  { key: "qa", title: "Q&A", blurb: "Real questions, answered by Rabbi Paltiel", scale: "100s of answers", slug: "qa" },
  { key: "glossaz", title: "Chassidic Glossary", blurb: "Classes dedicated to specific terms found in Chassidus.", scale: "Alphabetical", slug: "chassidic-glossary" },
  { key: "glossab", title: 'Chassidic Glossary in the order of the Hebrew Aleph beis באותיות אל"ף בית', blurb: "The same terms, arranged the way the sefarim arrange them", scale: "א to ת", slug: "chassidic-glossary-in-the-order-of-the-hebrew-aleph-beis" },
  { key: "talks", title: "Talks", blurb: "Talks and discussions on various topics in Yiddishkeit", scale: "By topic", slug: "life-lessons" },
  { key: "kabbalah", title: "Kabbalah, Chassidus, and its history", blurb: "Contextualizing Chassidus as a subject in Torah and Hashkafa", scale: "Understand the issues", slug: "thought-and-history" },
  { key: "rebbeim", title: "Lives of the Chabad Rebbeim", blurb: "The lives and stories of the Rebbeim, Rebbetzins, and famous Chassidim", scale: "Listen to the stories", slug: "thought-and-history/23-lives-of-the-chabad-rebbeim" },
];

/** Live subtree children per root slug, from /catalog/roots. */
function useRootChildren() {
  const [bySlug, setBySlug] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";
        const res = await fetch(`${base}/catalog/roots`, { headers: { Accept: "application/json" } });
        const json = res.ok ? await res.json() : null;
        if (!cancelled && json?.status) {
          const map = {};
          for (const r of json.data.roots || []) map[r.slug] = r.children || [];
          setBySlug(map);
        }
      } catch (_) {
        /* archive rows fall back to the category-page link */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return bySlug;
}

/** Progressive deep counts per node slug (6h-cached server-side). */
function useNodeCounts(slugs) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    slugs.slice(0, COUNT_FANOUT_CAP).forEach((slug) => {
      if (!slug) return;
      const node = String(slug).split("/").filter(Boolean).pop();
      getCatalogCounts(node).then((c) => {
        if (!cancelled && c) setCounts((prev) => (prev[slug] ? prev : { ...prev, [slug]: c }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [slugs.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return counts;
}

export default function CategoriesIndexPage() {
  const childrenBySlug = useRootChildren();
  const [view, setViewState] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) || "learning";
    } catch (_) {
      return "learning";
    }
  });
  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch (_) {}
  };
  const [sort, setSort] = useState("orig");
  const [expanded, setExpanded] = useState({ tanya: true }); // archive view: Tanya ships expanded
  const [openCard, setOpenCard] = useState(null); // learning view: one card open at a time

  // Rows are equal-height at rest (grid stretch). When a card opens, its
  // row-mates would stretch with it — so at open time we find the cards
  // sharing that row and pin them to their current height (top-aligned)
  // until the panel closes. Cleared on resize since rows can re-flow.
  const cardRefs = useRef({});
  const restHeights = useRef(null); // every card's height while nothing is open
  const [rowLock, setRowLock] = useState(null); // { mates: {key: height} }
  const toggleCard = (key, isOpen) => {
    if (isOpen) {
      setOpenCard(null);
      setTimeout(() => {
        setRowLock(null);
        restHeights.current = null;
      }, 320); // after the collapse transition
      return;
    }
    // Snapshot resting heights only when nothing is open — a card mid-
    // expansion must never be measured as a row-mate's "rest" height.
    if (!openCard || !restHeights.current) {
      const snap = {};
      Object.entries(cardRefs.current).forEach(([k, node]) => {
        if (!node) return;
        const panel = node.querySelector('[id^="cat-kids-"]');
        snap[k] = Math.round(node.getBoundingClientRect().height - (panel ? panel.offsetHeight : 0));
      });
      restHeights.current = snap;
    }
    const el = cardRefs.current[key];
    const top = el ? Math.round(el.getBoundingClientRect().top) : null;
    const mates = {};
    if (top !== null) {
      Object.entries(cardRefs.current).forEach(([k, node]) => {
        if (k === key || !node) return;
        if (Math.abs(Math.round(node.getBoundingClientRect().top) - top) <= 2) mates[k] = restHeights.current[k];
      });
    }
    // Keep the previously open row's pins through its collapse animation.
    setRowLock((prev) => ({ mates: { ...(prev?.mates || {}), ...mates } }));
    setTimeout(() => setRowLock({ mates }), 320);
    setOpenCard(key);
  };
  useEffect(() => {
    const clear = () => {
      setRowLock(null);
      setOpenCard(null);
      restHeights.current = null;
    };
    window.addEventListener("resize", clear);
    return () => window.removeEventListener("resize", clear);
  }, []);

  // Children for cards whose node isn't a taxonomy root (e.g. Lives of the
  // Chabad Rebbeim) — fetched on first expand via url-base, cached by slug.
  const [lazyKids, setLazyKids] = useState({});
  useEffect(() => {
    CATEGORIES.forEach((c) => {
      if (!(expanded[c.key] || openCard === c.key) || childrenBySlug[c.slug] || lazyKids[c.slug]) return;
      setLazyKids((prev) => (prev[c.slug] ? prev : { ...prev, [c.slug]: { loading: true, list: [] } }));
      resolvePermalink(c.slug)
        .then((r) => {
          const list =
            r?.kind === "category"
              ? r.children
                  .filter((k) => k.type === "category" || k.type === "series")
                  .map((k) => ({ type: k.type, title: k.title, slug: k.slug }))
              : [];
          setLazyKids((prev) => ({ ...prev, [c.slug]: { loading: false, list } }));
        })
        .catch(() => setLazyKids((prev) => ({ ...prev, [c.slug]: { loading: false, list: [] } })));
    });
  }, [expanded, openCard, childrenBySlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(
    () =>
      CATEGORIES.map((c) => ({
        ...c,
        children: childrenBySlug[c.slug] || lazyKids[c.slug]?.list || [],
        loading: !childrenBySlug[c.slug] && (lazyKids[c.slug]?.loading ?? !Object.keys(childrenBySlug).length),
      })),
    [childrenBySlug, lazyKids]
  );

  const childSlugs = useMemo(
    () => items.flatMap((c) => (expanded[c.key] ? c.children.map((k) => k.slug) : [])),
    [items, expanded]
  );
  const counts = useNodeCounts([...CATEGORIES.map((c) => c.slug), ...childSlugs]);

  const sortedItems = useMemo(() => {
    const copy = [...items];
    if (sort === "az") copy.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "count")
      copy.sort((a, b) => (counts[b.slug]?.classCount || 0) - (counts[a.slug]?.classCount || 0));
    return copy; // 'orig' = the reference's fixed order, untouched
  }, [items, sort, counts]);

  const countLabel = (slug) => {
    const c = counts[slug];
    return c ? `${(c.classCount || 0).toLocaleString()} classes` : "…";
  };

  const toggleBtn = (key, label) => {
    const active = view === key;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={active}
        onClick={() => setView(key)}
        className={
          active
            ? "h-11 px-5 rounded-[20px] border-0 bg-ic-blue text-white font-sans text-[16px] font-bold cursor-pointer focus-visible:outline-none focus-visible:shadow-ic-focus"
            : "h-11 px-5 rounded-[20px] border-0 bg-transparent text-ic-body font-sans text-[16px] font-semibold cursor-pointer transition-colors duration-150 ease-out hover:bg-black/5 focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
        }
      >
        {label}
      </button>
    );
  };

  const sortChip = ([key, label]) => {
    const active = sort === key;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={active}
        onClick={() => setSort(key)}
        className={
          active
            ? "flex-shrink-0 font-sans text-[14px] font-bold leading-5 text-white bg-ic-blue border border-ic-blue rounded-3xl px-4 h-11 cursor-pointer focus-visible:outline-none focus-visible:shadow-ic-focus"
            : "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray rounded-3xl px-4 h-11 cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
        }
      >
        {label}
      </button>
    );
  };

  return (
    <PublicLayout>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">All Categories</span>
        </nav>

        {/* Hero + view toggle */}
        <section className="flex items-end justify-between gap-5 flex-wrap">
          <div className="flex flex-col gap-2 min-w-[260px] flex-1">
            <h1 className="m-0 font-bold text-ic-navy tracking-[-0.4px]" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
              All Categories
            </h1>
            <p className="m-0 text-[18px] leading-7 text-ic-body max-w-[640px]">Discover your next class or series.</p>
          </div>
          <div role="group" aria-label="View" className="inline-flex bg-white border border-ic-borderGray rounded-3xl p-1 gap-1 flex-shrink-0">
            {toggleBtn("learning", "Discovery mode")}
            {toggleBtn("archive", "Classic view")}
          </div>
        </section>

        {/* ============ Learning view — rich cards, fixed order; each opens to its immediate children ============ */}
        {view === "learning" && (
          <div className="grid gap-5 pb-8" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))" }}>
            {items.map((cat) => {
              const isOpen = openCard === cat.key;
              const panelId = `cat-kids-${cat.key}`;
              const pinned = rowLock?.mates?.[cat.key];
              return (
                <div
                  key={cat.key}
                  ref={(node) => {
                    cardRefs.current[cat.key] = node;
                  }}
                  style={pinned ? { alignSelf: "start", height: `${pinned}px` } : undefined}
                  className={`relative bg-white border rounded-xl shadow-ic-raised flex flex-col min-h-[120px] transition-all duration-150 ease-out ${
                    isOpen ? "border-ic-borderGray shadow-ic-cta-hover" : "border-ic-lightGray hover:shadow-ic-cta-hover hover:border-ic-borderGray"
                  }`}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleCard(cat.key, isOpen)}
                    className="w-full text-center bg-transparent border-0 px-6 pt-7 pb-2 cursor-pointer font-sans flex flex-col items-center gap-3 rounded-t-xl focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
                  >
                    <CategoryIconDisc categoryKey={cat.key} size={96} />
                    <span className="flex flex-col gap-1.5 min-w-0 w-full">
                      <span className="text-[20px] font-bold leading-7 tracking-[-0.2px] text-ic-navy ic-wrap-anywhere">{cat.title}</span>
                      <span className="text-[15px] leading-[22px] text-ic-slate">{cat.blurb}</span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={`absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center transition-all duration-200 ease-out motion-reduce:transition-none ${
                        isOpen ? "bg-ic-blue text-white rotate-180" : "bg-ic-lightGray text-ic-slate"
                      }`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </span>
                  </button>

                  {/* Immediate children — height animates via the 0fr → 1fr grid trick */}
                  <div
                    id={panelId}
                    className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
                    style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                  >
                    <div className="overflow-hidden min-h-0">
                      <ul className="m-0 mx-6 mt-2 p-0 list-none flex flex-col border-t border-ic-lightGray" aria-label={`In ${cat.title}`}>
                        {cat.loading && cat.children.length === 0 && (
                          <li className="py-3 text-[14px] leading-5 text-ic-slate">Loading…</li>
                        )}
                        {!cat.loading && cat.children.length === 0 && (
                          <li className="py-3 text-[14px] leading-5 text-ic-slate">No sub-categories — everything is on the category page.</li>
                        )}
                        {cat.children.map((kid, i) => (
                          <li
                            key={`${kid.type}-${kid.slug}`}
                            className="flex flex-col gap-1 py-2.5 border-b border-[#F3F4F6] last:border-b-0 transition-opacity duration-200 ease-out motion-reduce:transition-none"
                            style={{ opacity: isOpen ? 1 : 0, transitionDelay: isOpen ? `${Math.min(i, 8) * 30}ms` : "0ms" }}
                          >
                            <Link
                              to={kid.type === "series" ? paths.series(kid.slug) : paths.permalink(kid.slug)}
                              className="flex items-center text-[16px] font-semibold leading-6 text-ic-body no-underline hover:text-ic-blue hover:no-underline min-h-[44px] ic-wrap-anywhere"
                            >
                              {kid.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <Link
                    to={paths.permalink(cat.slug)}
                    className="mt-auto mx-6 mb-3 pt-1 self-center text-[14px] font-bold text-ic-blue no-underline inline-flex items-center min-h-[44px] hover:underline"
                  >
                    {cat.scale} →
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* ============ Archive view — same 13, same order, live counts ============ */}
        {view === "archive" && (
          <div className="flex flex-col gap-4 pb-8">
            <div className="ic-rail flex items-center gap-2 overflow-x-auto">
              <span className="text-[14px] font-semibold text-ic-slate flex-shrink-0">Sort:</span>
              {[["orig", "Original order"], ["az", "A–Z"], ["count", "Most classes"]].map(sortChip)}
            </div>

            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
              {sortedItems.map((cat) => {
                const isOpen = Boolean(expanded[cat.key]);
                const hasKids = cat.children.length > 0;
                return (
                  <div key={cat.key} className="flex flex-col border-b border-[#F3F4F6] last:border-b-0">
                    <div className="flex items-center gap-2 px-3 py-1.5 transition-colors duration-150 ease-out hover:bg-ic-offwhite">
                      <button
                        type="button"
                        aria-label={isOpen ? `Collapse ${cat.title}` : `Expand ${cat.title}`}
                        aria-expanded={isOpen}
                        onClick={() => setExpanded(isOpen ? {} : { [cat.key]: true })}
                        className={`flex-shrink-0 w-11 h-11 rounded-xl bg-transparent border-0 inline-flex items-center justify-center cursor-pointer transition-colors duration-150 ease-out ${
                          isOpen ? "text-ic-blue hover:bg-[rgba(24,108,206,0.08)]" : "text-ic-slate hover:bg-black/5 hover:text-ic-blue"
                        } focus-visible:outline-none focus-visible:shadow-ic-focus-soft`}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 150ms ease-out" }}>
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                      <CategoryIcon categoryKey={cat.key} variant="small" size={28} className="flex-shrink-0" />
                      <Link to={paths.permalink(cat.slug)} className="flex-1 min-w-0 text-[18px] font-semibold leading-7 text-ic-navy py-2 no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                        {cat.title}
                      </Link>
                      <span className="flex-shrink-0 text-[14px] leading-5 text-ic-slate tabular-nums">{countLabel(cat.slug)}</span>
                    </div>
                    {isOpen && (
                      <div className="flex flex-col pl-10 pr-3 pb-2">
                        {cat.children.map((kid) => (
                          <div key={`${kid.type}-${kid.slug}`} className="flex items-center gap-3 py-1 pl-4 pr-2 border-l-2 border-ic-lightGray transition-colors duration-150 ease-out hover:bg-ic-offwhite">
                            <Link
                              to={kid.type === "series" ? paths.series(kid.slug) : paths.permalink(kid.slug)}
                              className="flex-1 min-w-0 text-[16px] font-semibold leading-6 text-ic-body py-1.5 no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere"
                            >
                              {kid.title}
                            </Link>
                            <span className="flex-shrink-0 text-[14px] leading-5 text-ic-slate tabular-nums">{countLabel(kid.slug)}</span>
                          </div>
                        ))}
                        {!hasKids && (
                          <div className="py-2 pl-4 border-l-2 border-ic-lightGray">
                            <Link to={paths.permalink(cat.slug)} className="text-[16px] text-ic-blue font-semibold inline-flex items-center min-h-[44px] hover:underline">
                              Open the category page for the full tree →
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </PublicLayout>
  );
}
